import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mail, Calendar, ShieldBan, ShieldCheck, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import type { ChatParticipant } from "@/hooks/useMessaging";

interface UserProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participant: ChatParticipant | null;
  currentUserId?: string | null;
}

export const UserProfileSheet = ({ open, onOpenChange, participant, currentUserId }: UserProfileSheetProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFullAvatar, setShowFullAvatar] = useState(false);
  const { toast } = useToast();

  const profile = participant?.profile;
  const otherUserId = participant?.user_id;

  useEffect(() => {
    if (!open || !currentUserId || !otherUserId) return;
    const checkBlocked = async () => {
      const { data } = await supabase
        .from("blocked_users")
        .select("id")
        .eq("user_id", currentUserId)
        .eq("blocked_user_id", otherUserId)
        .maybeSingle();
      setIsBlocked(!!data);
    };
    checkBlocked();
  }, [open, currentUserId, otherUserId]);

  if (!profile) return null;

  const { first_name, last_name, avatar_url, email } = profile;
  const displayName = [first_name, last_name].filter(Boolean).join(" ") || email || "Utente";

  const handleToggleBlock = async () => {
    if (!currentUserId || !otherUserId) return;
    setLoading(true);
    try {
      if (isBlocked) {
        await supabase
          .from("blocked_users")
          .delete()
          .eq("user_id", currentUserId)
          .eq("blocked_user_id", otherUserId);
        setIsBlocked(false);
        toast({ title: "Utente sbloccato", description: `${displayName} è stato sbloccato.` });
      } else {
        await supabase
          .from("blocked_users")
          .insert({ user_id: currentUserId, blocked_user_id: otherUserId });
        setIsBlocked(true);
        toast({ title: "Utente bloccato", description: `${displayName} è stato bloccato.` });
      }
    } catch {
      toast({ title: "Errore", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-background/95 backdrop-blur-2xl">
          <SheetHeader className="sr-only">
            <SheetTitle>Profilo utente</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col items-center pt-8 gap-5">
            <button
              onClick={() => avatar_url && setShowFullAvatar(true)}
              className="relative group"
            >
              <Avatar className="w-24 h-24 ring-4 ring-primary/20 transition-transform group-hover:scale-105 group-active:scale-95">
                <AvatarImage src={avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-2xl font-semibold">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {avatar_url && (
                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/10 transition-colors" />
              )}
            </button>
            <div className="text-center">
              <h2 className="text-xl font-bold">{displayName}</h2>
            </div>

            <div className="w-full space-y-1 mt-4">
              {email && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted/40">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm font-medium">{email}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted/40">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Membro dal</p>
                  <p className="text-sm font-medium">
                    {new Date(participant.joined_at).toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
              </div>

              {/* Block / Unblock */}
              {currentUserId && otherUserId && currentUserId !== otherUserId && (
                <button
                  onClick={handleToggleBlock}
                  disabled={loading}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl w-full transition-colors ${
                    isBlocked
                      ? "bg-green-500/10 hover:bg-green-500/20 text-green-600"
                      : "bg-destructive/10 hover:bg-destructive/20 text-destructive"
                  }`}
                >
                  {isBlocked ? <ShieldCheck className="w-5 h-5" /> : <ShieldBan className="w-5 h-5" />}
                  <span className="text-sm font-medium">
                    {isBlocked ? "Sblocca utente" : "Blocca utente"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Fullscreen Avatar Overlay */}
      <AnimatePresence>
        {showFullAvatar && avatar_url && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-xl flex items-center justify-center"
            onClick={() => setShowFullAvatar(false)}
          >
            <button
              onClick={() => setShowFullAvatar(false)}
              className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              src={avatar_url}
              alt={displayName}
              className="max-w-[90vw] max-h-[80vh] rounded-2xl object-contain shadow-2xl"
            />
            <p className="absolute bottom-10 text-white/80 text-lg font-semibold">{displayName}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
